/**
 * Per-file UI for the brief-attachment and IE-client upload flows.
 *
 * The pipeline that drives these lives in `presignedDropzones.ts`: presign → PUT
 * straight to S3 → complete, every step a typed tRPC call. Each file gets one
 * async closure there, so each file gets one tile handle here.
 *
 * ── Why a handle, and not four functions keyed by filename ──────────
 *
 * This module used to expose `add / progress / ready / failed` as four separate
 * functions that each re-found the tile from `hashCode(filename)`, having built
 * it as an HTML template literal with that id concatenated in. Four consequences,
 * all gone now that `addFile()` returns the tile it just made:
 *
 *  - **An XSS sink per tile.** `file.name` is the local filename, so a file
 *    called `a"><img src=x onerror=…>.jpg` executed on `innerHTML`. Nothing
 *    escaped it. Building nodes and passing strings to `append` makes them text.
 *  - **A pointless round-trip.** The delete button carried `data-delete-file` /
 *    `data-delete-upload` purely so its own click handler could read them back
 *    off the element it was attached to — values the enclosing closure held.
 *  - **A silent mis-target.** `hashCode` is a 32-bit hash of the filename, so
 *    uploading two files with the same name — a retry after a failure, or the
 *    same name from two folders — produced ONE id for both, and every progress
 *    tick went to whichever tile the DOM returned first.
 *  - **Two dead payloads.** `id="status-…"` and `<input class="file_asp_…">` are
 *    emitted here, by `views/pages/Mockup.tsx` AND by `modals/IeclientModal.tsx`,
 *    and read by nothing — no lookup, no CSS rule. Three renderers agreeing to
 *    emit something no one consumes.
 *
 * ⚠️ `hashCode` survives in exactly one place: the tile root's `id`, which is a
 * real cross-module contract — `mockupClient.ts`'s `ieclientDeleteFile` removes
 * by it. That contract has TWO id namespaces for one list: a server-rendered
 * tile is keyed by file id (`Mockup.tsx`, `IeclientModal.tsx`, `briefs.ts`), a
 * freshly-uploaded one by the filename hash, which is why that function takes
 * both an `id` and an `uploadid`. Collapsing it needs the list to have a single
 * renderer — the hydration waves in the plan.
 */

import { hashCode, perfectround } from "../utils/string";
import { api } from "../api";
import { toastSuccess, toastError } from "../ui/messages";
import { refreshModal } from "../ui/modal";
import { ieclientrefresh } from "./gallery";
import { getDomElement, h } from "../utils/dom";
import { briefFileRow } from "./briefs";
import { ieclientDeleteFile } from "./mockupClient";

/** One in-flight upload's tile. The caller holds it; nothing looks it up. */
export interface UploadTile<TReady> {
  progress(pct: number): void;
  ready(file: TReady): void;
  failed(message: string): void;
}

/** The server's record of a stored brief attachment. */
export interface BriefFileResult {
  filetype: string;
  filename: string;
  fileId: string;
  briefId: string;
}

/** The server's record of a processed IE-client source image. */
export interface IeclientFileResult {
  url: string;
  file_id: string;
  aspect: string;
}

/** A tile whose container was missing — every method is a no-op. */
function detachedTile<T>(): UploadTile<T> {
  return { progress: () => undefined, ready: () => undefined, failed: () => undefined };
}

// ── brief attachments ───────────────────────────────────────────────

/** Brief file upload started — render the placeholder row and return its handle. */
export function briefAddFile(name: string): UploadTile<BriefFileResult> {
  const container = getDomElement("brief-files");
  if (!container) return detachedTile();

  // Was `innerHTML === "<i>No files uploaded</i>"` — a string comparison against
  // markup another module writes, which any whitespace or tag change would break
  // silently. Asking whether a real row exists says the same thing and cannot rot.
  if (!container.querySelector(".brief-file")) container.replaceChildren();

  const pct = h("span", "", "0%");
  const root = h(
    "span",
    "brief-file",
    h("span", "brief-file-icon", h("i", "far fa-file")),
    h("span", "brief-file-name", `Uploading ${name} (`, pct, ")")
  );
  root.id = `brief-file-${hashCode(name)}`;
  container.prepend(root);

  return {
    progress(value) {
      pct.textContent = `${String(perfectround(value))}%`;
    },
    ready(file) {
      let icon = "";
      if (file.filetype === "jpg" || file.filetype === "png" || file.filetype === "tif") {
        icon = "-image";
      }
      if (file.filetype === "pdf") {
        icon = "-pdf";
      }

      // `replaceWith`, not `replaceChildren`: the finished row comes from the
      // SAME builder `briefGetFiles` uses, so this list stops having two
      // renderers. It also re-keys the row from `brief-file-<filename hash>` to
      // `brief-file-<fileId>`, which is what every other renderer of this list
      // uses — the placeholder's hash id was only ever needed while in flight.
      root.replaceWith(
        briefFileRow({
          fileId: file.fileId,
          briefId: file.briefId,
          filename: file.filename,
          iconClass: `fa-file${icon}`,
        })
      );
    },
    failed(message) {
      root.remove();
      toastError(message || "Upload failed");
    },
  };
}

// ── IE-client mockup uploads ────────────────────────────────────────

/** IE client file upload started — render the placeholder tile and return its handle. */
export function ieclientAddFile(name: string): UploadTile<IeclientFileResult> {
  const container = getDomElement("ieclient-target");
  if (!container) return detachedTile();

  const pct = h("span", "", "0%");
  const root = h(
    "div",
    "ieclient-file",
    h("div", "ieclient-file-icon", h("i", "fas fa-spinner")),
    h("div", "ieclient-file-name", `Uploading ${name} (`, pct, ")")
  );
  root.id = `ieclient-file-${hashCode(name)}`;
  container.prepend(root);

  return {
    progress(value) {
      pct.textContent = `${String(perfectround(value))}%`;
    },
    ready(file) {
      const thumb = document.createElement("img");
      thumb.src = file.url;

      const dot = h("span", "", h("i", "fas fa-circle"));
      dot.style.color = "#FFC251";

      const remove = h(
        "span",
        "ieclient-delete button tiny red",
        h("i", "far fa-trash-alt"),
        " Remove"
      );
      // The filename hash is the second id namespace `ieclientDeleteFile` needs:
      // this tile is keyed by it, not by the file id a server-rendered one
      // carries. It used to travel out as `data-delete-upload` and be read back
      // in here off `dataset`; the closure has both values already.
      remove.addEventListener("click", () => {
        void ieclientDeleteFile(file.file_id, String(hashCode(name)));
      });

      root.replaceChildren(
        thumb,
        // No `id="status-…"` and no `<input class="file_asp_…">`: nothing reads
        // either — not this bundle, not the server, not the stylesheet, which
        // styles `span.ieclient-file-status` by class.
        h(
          "div",
          "ieclient-file-status",
          h("span", "ieclient-file-status", dot, " Ready"),
          " ",
          remove
        )
      );

      // Increment the file count and activate the Next button.
      const filesEl = getDomElement<HTMLInputElement>("ieclient-files");
      if (filesEl) {
        filesEl.value = String((parseInt(filesEl.value) || 0) + 1);
      }
      const nextBtn = getDomElement("inext");
      if (nextBtn) {
        nextBtn.style.opacity = "";
      }

      void ieclientrefresh();
    },
    failed(message) {
      root.remove();
      toastError(message || "Upload failed");
      void ieclientrefresh();
    },
  };
}

// ── marketing files ─────────────────────────────────────────────────

export async function marketingdelete(id: string, onSuccess?: () => void): Promise<void> {
  if (!confirm("Are you sure you want to delete the file?")) return;
  try {
    await api.marketing.delete({ fileId: id });
    if (onSuccess) {
      onSuccess();
    } else {
      refreshModal();
    }
    toastSuccess("File deleted");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

// No `registerActions` block: nothing here is reachable through the `data-fn`
// dispatcher. Every consumer imports these directly — `presignedDropzones.ts`
// for the upload tiles, `modals/MarketingModal.tsx` for `marketingdelete`.
// The old registrations were ballast left over from the PHP templates that used
// to name them.
